export type Color = string;
import { theme } from '../../../../styles/tailwind-theme';

function isDark() {
  return (
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark')
  );
}

/**
 * Dark mode variants for colors that have poor contrast on dark backgrounds.
 * Only override colors where the light-mode value is too dark to see.
 */
const darkOverrides: Record<string, string> = {
  'neutral-base': '#818cf8', // indigo-400 — visible on gray-900
  'severity-critical-base': '#f87171', // red-400
  'severity-low-base': '#d4b896', // warm tan
};

function getColor(key: keyof typeof theme.extend.colors): string {
  const lightValue = theme.extend.colors[key];
  if (isDark() && darkOverrides[key]) {
    return darkOverrides[key];
  }
  return lightValue;
}

export const ChartColor = {
  get critical() {
    return getColor('severity-critical-base');
  },
  get high() {
    return getColor('severity-high-base');
  },
  get medium() {
    return getColor('severity-medium-base');
  },
  get low() {
    return getColor('severity-low-base');
  },
  get informational() {
    return getColor('neutral-base');
  },
  get unknown() {
    return getColor('severity-unknown-base');
  },
  get red() {
    return getColor('chart-red');
  },
  get blue() {
    return getColor('chart-blue');
  },
  get purple() {
    return getColor('chart-purple');
  },
  get pink() {
    return getColor('chart-pink');
  },
  get green() {
    return getColor('chart-green');
  },
  get teal() {
    return getColor('chart-teal');
  },
  get indigo() {
    return getColor('chart-indigo');
  },
  get orange() {
    return getColor('chart-orange');
  },
  get yellow() {
    return getColor('chart-yellow');
  },
  get lightGreen() {
    return getColor('chart-light-green');
  },
  get neutralBlue() {
    return getColor('neutral-base');
  },
};

export const defaultPalette: Color[] = [
  ChartColor.neutralBlue,
  ChartColor.red,
  ChartColor.teal,
  ChartColor.green,
  ChartColor.pink,
  ChartColor.orange,
  ChartColor.purple,
  ChartColor.lightGreen,
  ChartColor.indigo,
  ChartColor.yellow,
  ChartColor.blue,
];

export const severityPalette: Color[] = [
  ChartColor.critical,
  ChartColor.high,
  ChartColor.medium,
  ChartColor.low,
  ChartColor.informational,
  ChartColor.unknown,
];

export const ChartColorPalette = {
  default: defaultPalette,
  severity: severityPalette,
};
